import { inject } from '@angular/core';
import { ViewTransitionInfo } from '@angular/router';
import { PlatformService } from '@examdojo/platform';
import { getTransitionConfig, RouteTransition, TransitionDefinition } from '../route-transition.model';
import { isChildRoute, isParentRoute } from '../route-transition.util';

interface SlideRelativesOptions {
  direction: 'descendant' | 'ancestor';
}
export type SlideRelativesTransition = TransitionDefinition<RouteTransition.SlideRelatives, SlideRelativesOptions>;

/**
 * Applies the slide animation to relative routes
 * @returns false when not applicable
 * @returns true when applicable, but no transition is desired
 */
export function slideRelativesTransition({ from, to }: ViewTransitionInfo): string | boolean {
  const platformService = inject(PlatformService);

  const fromData = from.data['transition'] as SlideRelativesTransition | undefined;
  const toData = to.data['transition'] as SlideRelativesTransition | undefined;

  const definition = toData || fromData;
  const config = definition
    ? getTransitionConfig<RouteTransition.SlideRelatives, SlideRelativesOptions>(definition)
    : undefined;

  const shouldApply = platformService.isSmall() && config?.name === RouteTransition.SlideRelatives;

  if (!shouldApply) {
    return false;
  }

  let navigationDirection: 'down' | 'up' | 'unrelated' = 'unrelated';

  if (isChildRoute(to, from)) {
    navigationDirection = 'down';
  } else if (isParentRoute(to, from)) {
    navigationDirection = 'up';
  }

  if (navigationDirection === 'unrelated') {
    return true;
  }

  const allowedDirection = config?.options?.direction;

  if (allowedDirection) {
    if (navigationDirection === 'down' && allowedDirection !== 'descendant') {
      return false;
    }
    if (navigationDirection === 'up' && allowedDirection === 'ancestor') {
      return false;
    }
  }

  return navigationDirection === 'down' ? 'slide-right-to-left' : 'slide-left-to-right';
}
